/**
 * Full screen — with the WebKit spellings an iPad still uses — and the
 * one-time offer of it a tablet gets.
 *
 * The browser only grants full screen from inside a user gesture, so nothing
 * here can decide to go full screen on its own: the offer is a card with a
 * button, and the button is what asks.
 */

import { isTablet } from '../lib/device.ts';
import { prefsSnapshot, updatePrefs } from '../state/prefs.ts';
import * as ui from '../state/ui.ts';

interface FsDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface FsElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface StandaloneNavigator extends Navigator {
  standalone?: boolean;
}

const doc = (): FsDocument => document;
const root = (): FsElement => document.documentElement;

/** Whatever is full screen right now, or null. */
export const fullscreenElement = (): Element | null =>
  document.fullscreenElement ?? doc().webkitFullscreenElement ?? null;

/** Whether this browser lets a page go full screen at all. */
export const canFullscreen = (): boolean =>
  document.fullscreenEnabled === true ||
  doc().webkitFullscreenEnabled === true ||
  typeof root().webkitRequestFullscreen === 'function';

/** Installed to the home screen — already full screen, with no browser chrome to lose. */
export const isInstalled = (): boolean =>
  (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
  (navigator as StandaloneNavigator).standalone === true;

/** Asks for full screen. Resolves false when the browser said no. */
export async function enterFullscreen(): Promise<boolean> {
  const el = root();
  try {
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
}

export async function exitFullscreen(): Promise<void> {
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else await doc().webkitExitFullscreen?.();
  } catch {
    /* already out, or never in */
  }
}

export function toggleFullscreen(): void {
  if (fullscreenElement()) {
    void exitFullscreen();
    return;
  }
  void enterFullscreen().then((ok) => {
    if (!ok) ui.toast('Full screen is not available here');
  });
}

/**
 * Keeps the UI's idea of full screen in step with the document's, however it
 * was entered or left — the F key, the menu, the offer, or the Escape key the
 * browser handles itself. Returns a teardown function.
 */
export function wireFullscreen(): () => void {
  const sync = (): void => {
    const on = fullscreenElement() !== null;
    ui.setFullscreen(on);
    /* The offer has been answered by other means. */
    if (on) ui.setFullscreenOffer(false);
  };
  sync();
  document.addEventListener('fullscreenchange', sync);
  document.addEventListener('webkitfullscreenchange', sync);
  return () => {
    document.removeEventListener('fullscreenchange', sync);
    document.removeEventListener('webkitfullscreenchange', sync);
  };
}

/**
 * A tablet is offered full screen the first time the deck opens on it. Once
 * is the promise: the offer is recorded as made the moment it is shown, and
 * the menu carries the toggle from then on. A phone is not asked — the deck
 * is already all there is on that screen — and neither is a browser that
 * cannot do it, or an app already installed to the home screen.
 */
export function shouldOfferFullscreen(): boolean {
  if (!isTablet || prefsSnapshot().fullscreenOffered) return false;
  return canFullscreen() && !fullscreenElement() && !isInstalled();
}

/** Puts the offer up, and records that it has been made. */
export function offerFullscreen(): void {
  if (!shouldOfferFullscreen()) return;
  updatePrefs({ fullscreenOffered: true });
  ui.setFullscreenOffer(true);
}

/** The offer's "Full screen" button. */
export function acceptFullscreenOffer(): void {
  ui.setFullscreenOffer(false);
  void enterFullscreen().then((ok) => {
    if (!ok) ui.toast('Full screen is not available here');
  });
}

/** The offer's "Not now" button. */
export function declineFullscreenOffer(): void {
  ui.setFullscreenOffer(false);
}
